import { createContext, useState, useEffect, useCallback } from 'react';

const THEME_STORAGE_KEY = 'imago-theme';
const DEFAULT_THEME = 'dark';

/**
 * Theme context providing theme state and toggle functionality
 * Default value is null to detect usage outside provider
 */
export const ThemeContext = createContext(null);

/**
 * Get the initial theme from localStorage or default to dark
 * @returns {'light' | 'dark'} The initial theme
 */
const getInitialTheme = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return DEFAULT_THEME;
  }
  
  try {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme;
    }
  } catch (error) {
    // localStorage might be unavailable (e.g., private browsing)
    console.warn('Unable to access localStorage for theme:', error);
  }
  
  return DEFAULT_THEME;
};

/**
 * Apply theme class to document root element
 * @param {'light' | 'dark'} theme - The theme to apply
 */
const applyThemeToDocument = (theme) => {
  if (typeof document === 'undefined') return;
  
  const root = document.documentElement;
  
  // Remove both theme classes first
  root.classList.remove('light', 'dark');
  
  // Add the current theme class
  root.classList.add(theme);
};

/**
 * ThemeProvider component that manages theme state and persistence
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);
  
  // Apply theme immediately on mount (before first render)
  useEffect(() => {
    const initialTheme = getInitialTheme();
    applyThemeToDocument(initialTheme);
  }, []);
  
  // Apply theme to document when theme changes
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);
  
  // Persist theme to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (error) {
      console.warn('Unable to persist theme to localStorage:', error);
    }
  }, [theme]);
  
  /**
   * Set a specific theme
   * @param {'light' | 'dark'} newTheme - The theme to set
   */
  const setTheme = useCallback((newTheme) => {
    if (newTheme === 'light' || newTheme === 'dark') {
      setThemeState(newTheme);
    } else {
      console.warn(`Invalid theme value: ${newTheme}. Expected 'light' or 'dark'.`);
    }
  }, []);
  
  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = useCallback(() => {
    setThemeState((prevTheme) => (prevTheme === 'dark' ? 'light' : 'dark'));
  }, []);
  
  const value = {
    theme,
    toggleTheme,
    setTheme,
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export default ThemeProvider;
